import { RESPONSE_SHAPE } from '@eformulieren/model';
import { yXmlFragmentToProseMirrorRootNode } from '@tiptap/y-tiptap';
import * as Y from 'yjs';
import { COMMENT_MARK } from '../comments/CommentMark.js';
import { consentAnswer } from './consents.js';
import { richTextSchema } from '../field/controls/richtext/schema.js';
import { isMergedText, originOf, plainTextOf, plainTextOfFragment, richtextFragment, rowsList, orderedRows, textOf, valuesMap, } from './sectionDocument.js';
/**
 * De ProseMirror/TipTap-vorm van een rich-text-fragment, naast de platte tekst (R-50).
 *
 * Nodig omdat een atoomknoop (`figuur`, `referentie`) geen tekst bijdraagt aan `plainTextOf`
 * en dus bij een generatienaad stil verdwijnt als `waarde` de enige bron is. Hetzelfde
 * `richTextSchema()` als de gemonteerde editor (`Richtext.tsx`), anders verwijdert `y-tiptap`
 * een knooptype dat niet in het meegegeven schema staat stil uit het levende document
 * (`richtext/schema.ts`).
 *
 * **Zonder het opmerkinganker.** Dat anker hoort met opzet geen naad te overleven: een draadje
 * draagt zijn eigen `citaat` juist omdat het anker dat niet doet (`generaties.ts`, `compress`).
 * Zonder deze stap zou `structuur` dat stilzwijgend herzien: een opgelost draadje valt bij een
 * compactie uit `open_threads` (het gaat niet mee), maar zijn mark stond nog gewoon in de tekst en
 * zou anders alsnog meekomen via `structuur` — een highlight in de tekst zonder draadje erachter.
 */
export function structureOf(fragment) {
    const node = yXmlFragmentToProseMirrorRootNode(fragment, richTextSchema()).toJSON();
    return withoutCommentAnchors(node);
}
function withoutCommentAnchors(node) {
    const marks = node.marks?.filter((mark) => mark.type !== COMMENT_MARK);
    const content = node.content?.map(withoutCommentAnchors);
    return {
        ...node,
        ...(node.marks === undefined ? {} : { marks: marks === undefined || marks.length === 0 ? undefined : marks }),
        ...(content === undefined ? {} : { content }),
    };
}
/**
 * Het antwoord van één veld, of niets als er nog niets is ingevuld.
 *
 * Leeg is leeg: een veld dat er niet in staat is niet hetzelfde als een veld met
 * een lege waarde, en de evaluator behandelt beide als onbeantwoord (engine,
 * `isLeeg`). Een niet-aangeraakt criterium heeft dus geen oordeel en een slider
 * zonder aanraking geen waarde (ontwerp 6.2).
 */
export function answerOf({ veld: field, doc }) {
    const shape = RESPONSE_SHAPE[field.control];
    if (shape === 'geen' || shape === 'laag')
        return undefined;
    if (field.control === 'richtext') {
        const text = plainTextOf(doc, field.id);
        if (text.trim() === '')
            return undefined;
        const origin = originOf(doc, field.id);
        if (origin === undefined)
            return undefined;
        return { ...origin, waarde: text, structuur: structureOf(richtextFragment(doc, field.id)) };
    }
    if (shape === 'rijen')
        return rowsAnswer(doc, field.id);
    // Een akkoord per persoon staat in een eigen map, door de pod gezet (eisenboek 12.56, `consents.ts`).
    if (field.control === 'akkoordPerPersoon')
        return consentAnswer(doc, field.id);
    if (isMergedText(field.control)) {
        const origin = originOf(doc, field.id);
        if (origin === undefined)
            return undefined;
        return { ...origin, waarde: textAnswer(textOf(doc, field.id)) };
    }
    const raw = valuesMap(doc).get(field.id);
    if (raw === undefined)
        return undefined;
    const origin = originOf(doc, field.id);
    // Een waarde zonder herkomst is een halve schrijfactie; het schema van een
    // revisie weigert hem, dus hij hoort ook niet in de stand te belanden.
    if (origin === undefined)
        return undefined;
    switch (shape) {
        case 'waarden':
            return { ...origin, waarden: asValues(raw, field.id) };
        case 'documenten':
            return { ...origin, documenten: asDocuments(raw, field.id) };
        case 'waarde':
            return { ...origin, waarde: asSingle(raw, field.id) };
    }
}
/**
 * Rijen dragen zelf geen herkomst, de waarden in de rijen wel. Dat volgt het
 * schema (`Rows` in `revisie.ts`): wie een rij toevoegde is geen antwoord, wie een
 * veld in die rij vulde wel.
 */
function rowsAnswer(doc, fieldId) {
    // In de getoonde volgorde (R-40): de revisie en het bericht lezen de rijen zoals de invuller ze zag.
    const rows = orderedRows(rowsList(doc, fieldId));
    if (rows.length === 0)
        return undefined;
    return {
        rijen: rows.map((row) => {
            const rowId = row.get('rijId');
            if (typeof rowId !== 'string')
                throw new Error(`rij zonder rij-id in ${fieldId}`);
            return { rijId: rowId, waarden: rowValuesOf(row) };
        }),
    };
}
function rowValuesOf(row) {
    const values = {};
    for (const key of row.keys()) {
        if (key === 'rijId' || key.includes('@'))
            continue;
        const raw = row.get(key);
        if (raw === undefined)
            continue;
        const origin = originFromRow(row, key);
        if (origin === undefined)
            continue;
        // Een rich-text-kolom staat als fragment in de rijmap (zie `addRowTo`) en
        // gaat als platte tekst de stand in, net als een rich-text-veld buiten een rij, met
        // dezelfde `structuur`-aanvulling (zie `structureOf` hierboven).
        // Een tekstkolom staat als `Y.Text` in de rijmap (zie `addRowTo`) en gaat als gewone tekst de
        // stand in, met dezelfde lege-is-`null`-regel als een tekstveld buiten een rij.
        if (raw instanceof Y.Text) {
            values[key] = { ...origin, waarde: textAnswer(raw.toString()) };
            continue;
        }
        if (raw instanceof Y.XmlFragment) {
            const text = plainTextOfFragment(raw);
            if (text.trim() !== '')
                values[key] = { ...origin, waarde: text, structuur: structureOf(raw) };
            continue;
        }
        values[key] = Array.isArray(raw)
            ? // In een rij staan geen bijlagevelden: `RijVeld` in het model laat alleen
                // eenvoudige, toggle-, subgrid- en keuzevelden toe, dus een array is hier
                // altijd een meerwaardige keuze.
                { ...origin, waarden: asValues(raw, key) }
            : { ...origin, waarde: asSingle(raw, key) };
    }
    return values;
}
/**
 * Het antwoord van een tekstveld: de tekst, of `null` als hij leeg is.
 *
 * Een leeg tekstveld met een herkomst is een veld dat iemand heeft leeggemaakt. In de waardenmap
 * stond dat als `null` (het `onWaarde(undefined)` van de control), en de stand houdt die vorm: de
 * evaluator behandelt beide als leeg (`isEmpty`), en een revisie legt vast dat en door wie het veld
 * werd leeggemaakt (R-97). Zo verandert de overstap naar `Y.Text` niets aan wat de opslag ziet.
 */
function textAnswer(text) {
    return text === '' ? null : text;
}
function originFromRow(row, key) {
    const by = row.get(`${key}@door`);
    const changedAt = row.get(`${key}@op`);
    if (typeof by !== 'string' || typeof changedAt !== 'string')
        return undefined;
    return { door: by, gewijzigdOp: changedAt };
}
function asSingle(raw, fieldId) {
    if (raw === null)
        return null;
    const kind = typeof raw;
    if (kind === 'string' || kind === 'number' || kind === 'boolean') {
        return raw;
    }
    throw new Error(`${fieldId} houdt geen enkele waarde maar ${kind}`);
}
function asValues(raw, fieldId) {
    if (!Array.isArray(raw))
        throw new Error(`${fieldId} houdt geen lijst waarden`);
    return raw.map((w) => {
        if (typeof w === 'string' || typeof w === 'number')
            return w;
        throw new Error(`${fieldId} houdt een waarde die geen tekst of getal is`);
    });
}
function asDocuments(raw, fieldId) {
    if (!Array.isArray(raw))
        throw new Error(`${fieldId} houdt geen lijst verwijzingen`);
    return raw.map((d) => {
        const v = d;
        if (typeof v.hash !== 'string' || typeof v.bestandsnaam !== 'string' || typeof v.grootteBytes !== 'number') {
            throw new Error(`${fieldId} houdt een verwijzing zonder hash, naam of omvang`);
        }
        return { hash: v.hash, bestandsnaam: v.bestandsnaam, grootteBytes: v.grootteBytes };
    });
}
/**
 * De stand over alle secties samen. Velden zonder antwoord staan er niet in, want
 * de evaluator behandelt afwezig en leeg gelijk en een revisie legt vast wat er
 * werkelijk stond.
 */
export function stateOf(fields) {
    const state = {};
    for (const place of fields) {
        const response = answerOf(place);
        if (response !== undefined)
            state[place.veld.id] = response;
    }
    return state;
}
//# sourceMappingURL=state.js.map